import React, { useMemo } from 'react';
import { IStandardDocumentVersionData } from 'types/order/documents/shared/interfaces';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from 'API/documentCategories/DocumentCategoriesApi';
import { Box, Menu, MenuItem, MenuPopper, Paragraph, Tooltip, TooltipPopper } from '@zillow/constellation';
import { useGetSetOfAllSigningAppointmentDocuments } from '../DeleteDocumentModal/useGetSetOfAllSigningAppointmentDocuments';
import { ErrorBoundaryConstellation } from '../../../../../constellationExtended/ErrorBoundary/ErrorBoundaryConstellation';

export interface IDocumentActivityCellProps {
  document: IStandardDocumentVersionData;
  onShowDelete: () => void;
  setShowGenerateLinkModal: (val: IStandardDocumentVersionData | null) => void;
  onDetailsClick: () => void;
  onFinalizeDocument: (docId: string, versionId: string) => void;
  setDocToFinalize: (val: IStandardDocumentVersionData | null) => void;
}

export const DocumentActivityCell: React.FC<IDocumentActivityCellProps> = ({
  document,
  onShowDelete,
  setShowGenerateLinkModal,
  onDetailsClick,
  onFinalizeDocument,
  setDocToFinalize,
}) => {
  const { data: { documentCategoriesByName } = defaultNormalizedDocumentCategoriesData } =
    useListDocumentCategoriesQuery();
  const setOfAllSigningAppointmentDocuments = useGetSetOfAllSigningAppointmentDocuments();

  const actionItems = useMemo(() => {
    let items: {
      text: string;
      onClick?: () => void;
      tooltip?: React.ReactNode;
      disabled?: boolean;
      className?: string;
      'data-test-id'?: string;
    }[] = [
      {
        text: 'Details',
        onClick: onDetailsClick,
      },
      {
        text: 'Generate Link',
        onClick: () => setShowGenerateLinkModal(document),
      },
    ];

    if (setOfAllSigningAppointmentDocuments.has(document.id)) {
      items.push({
        text: 'Delete',
        disabled: true,
        'data-test-id': 'deletion-not-allowed',
        tooltip: (
          <Tooltip
            body={
              <Box>
                <Paragraph>This document is associated with a signing appointment.</Paragraph>
                <Paragraph>It cannot be deleted.</Paragraph>
              </Box>
            }
          />
        ),
      });
    } else {
      items.push({
        text: 'Delete',
        'data-test-id': 'deletion-allowed',
        onClick: onShowDelete,
        className: '!text-red-600',
      });
    }

    if (documentCategoriesByName[document.category]?.isFinalizable) {
      items.splice(3, 0, {
        text: 'Finalize',
        onClick: () => {
          onFinalizeDocument(document.id, document.versionId);
          setDocToFinalize(document);
        },
      });
    }
    return items;
  }, [
    onDetailsClick,
    setShowGenerateLinkModal,
    onShowDelete,
    onFinalizeDocument,
    setDocToFinalize,
    document,
    documentCategoriesByName,
    setOfAllSigningAppointmentDocuments,
  ]);
  return (
    <ErrorBoundaryConstellation>
      <MenuPopper
        shouldCloseOnViewportLeave
        data-test-id={`document-activity-cell-${document.id}`}
        triggered={
          <Menu>
            {actionItems.map((item) => (
              <MenuItem
                key={item.text}
                onClick={item.onClick}
                className={item.className}
                disabled={item.disabled}
                data-test-id={item['data-test-id']}
              >
                {item.text}{' '}
                {item.tooltip && (
                  <TooltipPopper data-test-id={item['data-test-id'] + '-tooltip-button'} triggered={item.tooltip} />
                )}
              </MenuItem>
            ))}
          </Menu>
        }
      />
    </ErrorBoundaryConstellation>
  );
};
